import React, { useState, useEffect } from 'react';
import { Plus, X } from 'lucide-react';
import { scansApi } from '../services/scansApi';
import { useAuthStore } from '../stores/authStore';

interface ProfilePair {
    key: string;
    value: string;
}

interface Props {
    className?: string;
    scanId: string;
    tenantId: string;
    projectId: string;
    assetId: string;
}

const ApplicationProfiles: React.FC<Props> = ({ className, scanId, tenantId, projectId, assetId }) => {
    const { user } = useAuthStore();
    const [profiles, setProfiles] = useState<ProfilePair[]>([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [newPairs, setNewPairs] = useState<ProfilePair[]>([{ key: '', value: '' }]);
    const [profileExists, setProfileExists] = useState(false);
    const [saving, setSaving] = useState(false);

    // Check if user can manage application profiles
    const canManageProfiles = user?.roles?.includes('ADMIN') || user?.roles?.includes('PENTESTER');

    // Fetch application profile on mount
    useEffect(() => {
        fetchApplicationProfile();
    }, [scanId]);

    const fetchApplicationProfile = async () => {
        if (!scanId) {
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const response = await scansApi.getApplicationProfile(scanId);
            console.log('Application Profile Response:', response);

            if (response.success) {
                // Handle both 'data' and 'response' keys from API
                const profileData = (response as any).response || response.data;
                const attributes = profileData?.applicationProfileAttributes || [];
                console.log('Loaded application profile attributes:', attributes);

                if (attributes.length > 0) {
                    setProfiles(attributes);
                    setProfileExists(true);
                } else {
                    console.log('No application profile attributes found');
                    setProfiles([]);
                    setProfileExists(false);
                }
            } else {
                console.log('No application profile found or empty response');
                setProfiles([]);
                setProfileExists(false);
            }
        } catch (error: any) {
            console.error('Failed to fetch application profile:', error);
            // If it's a 404, the profile doesn't exist yet
            if (error?.status === 404 || error?.response?.status === 404) {
                console.log('Profile does not exist yet (404)');
                setProfiles([]);
                setProfileExists(false);
            } else {
                console.error('Error fetching profile:', error);
                setProfiles([]);
                setProfileExists(false);
            }
        } finally {
            setLoading(false);
        }
    };

    const openModal = () => {
        // Pre-fill with existing profiles if they exist, otherwise start with one empty pair
        if (profiles.length > 0) {
            setNewPairs([...profiles, { key: '', value: '' }]);
        } else {
            setNewPairs([{ key: '', value: '' }]);
        }
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setNewPairs([{ key: '', value: '' }]);
    };

    const handleAddPair = () => {
        setNewPairs([...newPairs, { key: '', value: '' }]);
    };

    const handleRemovePair = (idx: number) => {
        if (newPairs.length > 1) {
            setNewPairs(newPairs.filter((_, i) => i !== idx));
        }
    };

    const handlePairChange = (idx: number, field: 'key' | 'value', value: string) => {
        const updated = [...newPairs];
        updated[idx][field] = value;
        setNewPairs(updated);
    };

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        const validPairs = newPairs.filter(pair => pair.key.trim() && pair.value.trim());

        if (validPairs.length === 0) {
            return;
        }

        setSaving(true);
        try {
            if (profileExists) {
                // Update existing profile with all pairs (including modified ones)
                // Remove _id field from pairs before sending to API
                const pairsWithoutId = validPairs.map(({ key, value }) => ({ key, value }));
                console.log('Updating application profile with:', pairsWithoutId);
                const response = await scansApi.updateApplicationProfile(scanId, {
                    applicationProfileAttributes: pairsWithoutId
                });
                console.log('Update response:', response);

                if (response.success) {
                    // Handle both 'data' and 'response' keys from API
                    const profileData = (response as any).response || response.data;
                    const attributes = profileData?.applicationProfileAttributes || [];
                    console.log('Updated attributes:', attributes);
                    setProfiles(attributes);
                }
            } else {
                // Create new profile
                const pairsWithoutId = validPairs.map(({ key, value }) => ({ key, value }));
                console.log('Creating application profile with:', pairsWithoutId);
                const response = await scansApi.createApplicationProfile({
                    scanId,
                    tenantId,
                    projectId,
                    assetId,
                    applicationProfileAttributes: pairsWithoutId
                });
                console.log('Create response:', response);

                if (response.success) {
                    // Handle both 'data' and 'response' keys from API
                    const profileData = (response as any).response || response.data;
                    const attributes = profileData?.applicationProfileAttributes || [];
                    console.log('Created attributes:', attributes);
                    setProfiles(attributes);
                    setProfileExists(true);
                }
            }
            closeModal();
        } catch (error) {
            console.error('Failed to save application profile:', error);
            alert('Failed to save application profile. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const wrapperClass = className ?? 'bg-white dark:bg-gray-800 rounded-lg shadow p-6';

    return (
        <div className={`${wrapperClass} flex flex-col`}>
            <div className="flex justify-between items-center mb-4 flex-shrink-0">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Application Profiles</h2>
                {canManageProfiles && (
                    <button
                        className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                        onClick={openModal}
                    >
                        <Plus className="h-4 w-4 mr-2" />
                        Add / Update
                    </button>
                )}
            </div>
            <div className="text-sm overflow-y-auto h-64">
                {loading ? (
                    <div className="text-gray-500 dark:text-gray-400 text-center py-4">
                        Loading...
                    </div>
                ) : profiles.length === 0 ? (
                    <div className="text-gray-500 dark:text-gray-400 text-center py-4">
                        No application profiles added yet.
                    </div>
                ) : (
                    profiles.map((pair, idx) => (
                        <div className="flex gap-2 mb-2" key={idx}>
                            <span className="text-gray-500 dark:text-gray-400 flex-shrink-0 w-32 sm:w-40 break-words">{pair.key}</span>
                            <span className="text-gray-500 dark:text-gray-400 flex-shrink-0">:</span>
                            <span className="text-gray-900 dark:text-white flex-1 break-words overflow-wrap-anywhere">{pair.value}</span>
                        </div>
                    ))
                )}
            </div>

            {/* Modal */}
            {showModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    <div
                        className="absolute inset-0 bg-black/40"
                        onClick={closeModal}
                    />
                    <div className="relative z-10 w-full max-w-2xl mx-4 bg-white dark:bg-gray-800 rounded-lg shadow-lg">
                        {/* Header */}
                        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                Add Application Profiles
                            </h3>
                            <button
                                onClick={closeModal}
                                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                            >
                                ✕
                            </button>
                        </div>

                        {/* Form */}
                        <form
                            onSubmit={handleSave}
                            className="px-6 py-4 max-h-[70vh] overflow-y-auto"
                        >
                            <div className="space-y-3">
                                {newPairs.map((pair, idx) => (
                                    <div key={idx} className="flex gap-2 items-center">
                                        <input
                                            type="text"
                                            placeholder="Key (e.g., Application Name)"
                                            value={pair.key}
                                            onChange={e => handlePairChange(idx, 'key', e.target.value)}
                                            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white text-sm"
                                            required
                                        />
                                        <input
                                            type="text"
                                            placeholder="Value"
                                            value={pair.value}
                                            onChange={e => handlePairChange(idx, 'value', e.target.value)}
                                            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white text-sm"
                                            required
                                        />
                                        <div className="flex gap-1">
                                            <button
                                                type="button"
                                                onClick={handleAddPair}
                                                className="inline-flex items-center justify-center h-10 w-10 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                                                title="Add another pair"
                                            >
                                                <Plus className="h-4 w-4" />
                                            </button>
                                            {newPairs.length > 1 && (
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemovePair(idx)}
                                                    className="inline-flex items-center justify-center h-10 w-10 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 bg-white dark:bg-gray-700 hover:bg-red-50 dark:hover:bg-red-900/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                                                    title="Remove this pair"
                                                >
                                                    <X className="h-4 w-4" />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Footer */}
                            <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                                <button
                                    type="button"
                                    onClick={closeModal}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={saving}
                                    className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {saving ? 'Saving...' : 'Save Profiles'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ApplicationProfiles;
